<template>
  <v-snackbar
    :model-value="open"
    location="bottom right"
    :timeout="-1"
    color="primary"
    class="chapter-sync-floating"
    elevation="4"
  >
    <div class="chapter-sync-floating__content">
      <p v-if="displaySummary" class="chapter-sync-floating__summary text-body-2 mb-2">
        {{ displaySummary }}
      </p>
      <ul v-if="displayDetailItems.length" class="chapter-sync-floating__list text-body-2">
        <li
          v-for="item in displayDetailItems"
          :key="`${item.projectId}:${item.chapterId}`"
          class="chapter-sync-floating__row"
        >
          <v-icon
            v-if="item.phase === 'synced'"
            size="16"
            class="chapter-sync-floating__row-icon"
          >
            mdi-check-circle
          </v-icon>
          <span>{{ item.title }}</span>
          <span v-if="item.message" class="chapter-sync-floating__detail-emphasis">
            — {{ item.message }}
          </span>
        </li>
      </ul>
      <div class="chapter-sync-floating__actions mt-2">
        <v-btn
          v-if="displayPrimaryItem"
          size="small"
          variant="flat"
          color="surface"
          @click="goToEditor(displayPrimaryItem)"
        >
          Ir al editor
        </v-btn>
        <v-btn
          v-if="showDismissButton"
          size="small"
          variant="flat"
          color="surface"
          @click="dismissSnackbar"
        >
          <v-icon start size="18">mdi-close</v-icon>
          {{ dismissButtonLabel }}
        </v-btn>
      </div>
    </div>
  </v-snackbar>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import BackupService from '@/services/backup'
import {
  getActiveEditorChapter,
  subscribeChapterPersistence,
} from '@/services/chapterPersistenceOrchestrator'
import { useChapterSyncStore, type ChapterSyncItem } from '@/stores/chapterSync'
import { useConnectionStatus } from '@/composables/useConnectionStatus'
import { resolveSyncPhase, type ChapterSyncPhase } from '@/utils/chapterSyncPhase'

/** How long each successfully uploaded chapter row stays visible in the snackbar. */
const ROW_SUCCESS_VISIBLE_MS = 4_000

type ChapterSyncDisplayItem = Omit<ChapterSyncItem, 'phase'> & {
  phase: ChapterSyncPhase | 'synced'
}

const chapterSyncStore = useChapterSyncStore()
chapterSyncStore.ensureSubscribed()

const { items, hasVisibleActivity } = storeToRefs(chapterSyncStore)
const { isOnline } = useConnectionStatus()
const router = useRouter()
const dismissed = ref(false)
const hadVisibleActivity = ref(false)
const lingeringSuccessRows = ref<ChapterSyncDisplayItem[]>([])
/** Last seen dirty/uploading phase per chapter (from backups, including while the editor hides the row). */
const prevUploadPhaseByKey = new Map<string, ChapterSyncPhase>()

const rowLingerTimers = new Map<string, ReturnType<typeof setTimeout>>()

function itemKey(item: { projectId: string; chapterId: string }): string {
  return `${item.projectId}:${item.chapterId}`
}

function isUploadingPhase(phase: ChapterSyncPhase): boolean {
  return phase === 'dirty' || phase === 'uploading'
}

function isActiveEditorChapter(projectId: string, chapterId: string): boolean {
  const active = getActiveEditorChapter()
  return active?.projectId === projectId && active?.chapterId === chapterId
}

function purgeLingerForActiveEditor() {
  const active = getActiveEditorChapter()
  if (!active) return
  removeLingeringRow(itemKey(active))
}

function clearRowLingerTimers() {
  for (const timer of rowLingerTimers.values()) {
    clearTimeout(timer)
  }
  rowLingerTimers.clear()
}

function dismissSnackbar() {
  dismissed.value = true
  lingeringSuccessRows.value = []
  prevUploadPhaseByKey.clear()
  clearRowLingerTimers()
}

function removeLingeringRow(key: string) {
  lingeringSuccessRows.value = lingeringSuccessRows.value.filter(
    (row) => itemKey(row) !== key,
  )
  const timer = rowLingerTimers.get(key)
  if (timer) {
    clearTimeout(timer)
    rowLingerTimers.delete(key)
  }
}

function addLingeringSuccess(item: ChapterSyncItem) {
  const key = itemKey(item)
  if (lingeringSuccessRows.value.some((row) => itemKey(row) === key)) return

  lingeringSuccessRows.value = [
    ...lingeringSuccessRows.value,
    {
      projectId: item.projectId,
      chapterId: item.chapterId,
      title: item.title,
      phase: 'synced',
      message: 'Guardado en el servidor',
    },
  ]

  const timer = setTimeout(() => {
    removeLingeringRow(key)
  }, ROW_SUCCESS_VISIBLE_MS)
  rowLingerTimers.set(key, timer)
}

function isSuccessfulSyncPhase(phase: ChapterSyncPhase): boolean {
  return phase === 'synced_grace' || phase === 'offline_mirror'
}

function syncFromPersistence() {
  chapterSyncStore.refresh()

  for (const backup of BackupService.listChapterBackups()) {
    const phase = resolveSyncPhase(backup)
    const key = itemKey(backup)
    const previous = prevUploadPhaseByKey.get(key)

    if (
      previous
      && isUploadingPhase(previous)
      && isSuccessfulSyncPhase(phase)
      && !isActiveEditorChapter(backup.projectId, backup.chapterId)
    ) {
      addLingeringSuccess({
        projectId: backup.projectId,
        chapterId: backup.chapterId,
        title: backup.title || 'Capítulo sin título',
        phase: previous,
      })
    }

    if (isUploadingPhase(phase)) {
      prevUploadPhaseByKey.set(key, phase)
    } else {
      prevUploadPhaseByKey.delete(key)
    }
  }

  purgeLingerForActiveEditor()
}

function summaryLineFromItems(syncItems: ChapterSyncDisplayItem[]): string {
  const count = syncItems.length
  if (count === 0) return ''

  const uploading = syncItems.filter(
    (item) => item.phase !== 'synced' && isUploadingPhase(item.phase),
  ).length
  if (uploading > 0) {
    return uploading === 1
      ? 'Guardando 1 capítulo en segundo plano…'
      : `Guardando ${uploading} capítulos en segundo plano…`
  }

  const synced = syncItems.filter((item) => item.phase === 'synced').length
  if (synced > 0) {
    return synced === 1
      ? '1 capítulo guardado en el servidor'
      : `${synced} capítulos guardados en el servidor`
  }

  if (syncItems.some((item) => item.phase === 'offline') && !isOnline.value) {
    return count === 1
      ? 'Sin conexión: un capítulo quedó pendiente de subir.'
      : `Sin conexión: ${count} capítulos quedaron pendientes de subir.`
  }
  if (syncItems.some((item) => item.phase === 'auth_blocked')) {
    return 'Vuelve a entrar para subir los cambios pendientes.'
  }
  if (syncItems.some((item) => item.phase === 'needs_editor')) {
    return 'Un capítulo necesita confirmación en el editor.'
  }
  if (syncItems.some((item) => item.phase === 'conflict')) {
    return 'No se pudo guardar por un posible conflicto de versiones. Abre el editor para revisarlo.'
  }
  return count === 1
    ? 'No se pudo guardar un capítulo.'
    : `No se pudieron guardar ${count} capítulos.`
}

function primaryItemFromList(
  syncItems: ChapterSyncDisplayItem[],
): ChapterSyncDisplayItem | null {
  const priority: ChapterSyncDisplayItem['phase'][] = [
    'needs_editor',
    'conflict',
    'auth_blocked',
    'offline',
    'failed',
    'uploading',
    'dirty',
  ]
  for (const phase of priority) {
    const match = syncItems.find((item) => item.phase === phase)
    if (match) return match
  }
  return null
}

const backgroundLingerRows = computed(() =>
  lingeringSuccessRows.value.filter(
    (row) => !isActiveEditorChapter(row.projectId, row.chapterId),
  ),
)

const displayDetailItems = computed((): ChapterSyncDisplayItem[] => {
  const active = items.value as ChapterSyncDisplayItem[]
  const activeKeys = new Set(active.map(itemKey))
  const linger = backgroundLingerRows.value.filter((row) => !activeKeys.has(itemKey(row)))
  return [...active, ...linger]
})

const displaySummary = computed(() => summaryLineFromItems(displayDetailItems.value))

const displayPrimaryItem = computed(() => primaryItemFromList(displayDetailItems.value))

const open = computed(() => {
  if (dismissed.value) return false
  // Background sync only: chapterSync already omits the open editor's dirty/uploading rows.
  return hasVisibleActivity.value || backgroundLingerRows.value.length > 0
})

/** No pending uploads in the queue; user can dismiss after seeing success rows. */
const showCloseAfterBackup = computed(() => !hasVisibleActivity.value)

const showDismissButton = computed(() => true)

const dismissButtonLabel = computed(() =>
  showCloseAfterBackup.value ? 'Cerrar' : 'Ocultar aviso',
)

watch(hasVisibleActivity, (active) => {
  if (active && !hadVisibleActivity.value) {
    dismissed.value = false
  }
  hadVisibleActivity.value = active
})

let unsubscribePersistence: (() => void) | null = null

onMounted(() => {
  unsubscribePersistence = subscribeChapterPersistence(() => {
    syncFromPersistence()
  })
  syncFromPersistence()
})

async function goToEditor(item: ChapterSyncDisplayItem) {
  await router.push({
    name: 'project',
    params: { projectId: item.projectId },
    query: {
      tab: 'editor',
      chapterId: item.chapterId,
    },
  })
}

onBeforeUnmount(() => {
  unsubscribePersistence?.()
  clearRowLingerTimers()
})
</script>

<style scoped>
.chapter-sync-floating__list {
  margin: 0;
  padding-left: 0;
  max-width: 22rem;
  list-style: none;
}

.chapter-sync-floating__row {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
}

.chapter-sync-floating__row-icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.chapter-sync-floating__content {
  max-width: 24rem;
  color: rgb(var(--v-theme-on-primary));
}

.chapter-sync-floating__detail-emphasis {
  opacity: 0.85;
}
</style>
